import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["defaultConnection"];
import ConnectionKeys from 'automation-ui-action-features/constants/ConnectionKeys';
import ConnectionMixin, { ConnectionMixinDefaultValues } from 'automation-ui-action-features/records/connection/ConnectionMixin';
import BranchConnector from 'automation-ui-action-features/records/connection/BranchConnector';
// Prevent cyclical dependency. Copied from recordsUtils.ts
const DEFAULT_BRANCH_INDEX = -1;
function isDefaultBranchIndex(branchIndex) {
  return branchIndex === DEFAULT_BRANCH_INDEX;
}
/**
 * An edge is a string of the form:
 *   '${previousActionId}-edge-${edgeModifier}'
 * Returns DEFAULT_BRANCH_INDEX for the "NO" branch
 */
function getBranchIndex(edgeId) {
  return Number(edgeId.split('-edge-')[1]) - 2;
}
// End copy from recordsUtils.ts

export const BranchConnectionMixinDefaultValues = Object.assign({}, ConnectionMixinDefaultValues, {
  [ConnectionKeys.DEFAULT_CONNECTION]: null,
  [ConnectionKeys.DEFAULT_BRANCH_NAME]: undefined
});
export function branchConnectionFromJSON(_ref) {
  let {
      defaultConnection
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return Object.assign({}, rest, {
    defaultConnection: defaultConnection && new BranchConnector(defaultConnection)
  });
}

/**
 * `BranchConnectionMixin` defines common behavior among the connection objects
 * found on branching actions, such as LIST_BRANCH and STATIC_BRANCH. This
 * behavior is realized by records like `ListBranchConnection` and
 * `StaticBranchConnection`.
 */
function BranchConnectionMixin(superClass) {
  class BranchConnectionMixinClass extends ConnectionMixin(superClass) {
    isBranchConnection() {
      return true;
    }
    getConnectorByEdgeId(edgeId) {
      const branchIndex = getBranchIndex(edgeId);
      return this.getConnectorByBranchIndex(branchIndex);
    }
    setConnectorByEdgeId(edgeId, connector) {
      const branchIndex = getBranchIndex(edgeId);
      return this.setConnectorByBranchIndex(branchIndex, connector);
    }
    getDefaultConnector() {
      return this.get(ConnectionKeys.DEFAULT_CONNECTION);
    }
    setDefaultConnector(connector) {
      return this.set(ConnectionKeys.DEFAULT_CONNECTION, connector);
    }
    getConnectorByBranchIndex(branchIndex = DEFAULT_BRANCH_INDEX) {
      if (isDefaultBranchIndex(branchIndex)) {
        return this.getDefaultConnector();
      } else {
        return this.getBranches().get(branchIndex).getConnector();
      }
    }
    setConnectorByBranchIndex(branchIndex = DEFAULT_BRANCH_INDEX, connector) {
      if (isDefaultBranchIndex(branchIndex)) {
        return this.setDefaultConnector(connector);
      } else {
        return this.setBranches(this.getBranches().update(branchIndex, branch => branch.setConnector(connector)));
      }
    }
    getDefaultBranchName() {
      return this.get(ConnectionKeys.DEFAULT_BRANCH_NAME);
    }
    setDefaultBranchName(defaultBranchName) {
      return this.set(ConnectionKeys.DEFAULT_BRANCH_NAME, defaultBranchName);
    }
    getConnectors() {
      return [this.getDefaultConnector(), ...this.getBranches().toArray().map(branch => branch.getConnector())];
    }
    updateConnectors(updater) {
      return this.setDefaultConnector(updater(this.getDefaultConnector())).setBranches(this.getBranches().map(branch => branch.updateConnector(connector => updater(connector))).toList());
    }

    /**
     * If the root loop node id is provided, ensure the
     * branch connection object has no null connectors.
     * This is a requirement of loop actions to avoid
     * stranding an executor.
     */
    sanitizeBranchConnection(rootLoopNodeId) {
      if (!rootLoopNodeId) {
        return this;
      }
      const newBranches = this.getBranches().map(branch => branch.sanitizeConnector({
        rootLoopNodeId
      })).toList();
      const defaultConnector = this.getDefaultConnector() || new BranchConnector();
      const newDefaultConnector = defaultConnector.sanitizeConnector({
        rootLoopNodeId
      });
      return this.setBranches(newBranches).setDefaultConnector(newDefaultConnector);
    }
  }
  return BranchConnectionMixinClass;
}
export default BranchConnectionMixin;